export type LeadCaseModuleKey =
  | 'family_law'
  | 'civil_litigation'
  | 'personal_injury_workers_comp'
  | 'employment'
  | 'criminal'
  | 'immigration'
  | 'estate_probate';

export type LeadCaseModuleFieldValue = string | number | boolean | string[];

export type LeadCaseModules = Partial<Record<LeadCaseModuleKey, Record<string, LeadCaseModuleFieldValue>>>;

export interface LeadCaseModuleFieldDefinition {
  key: string;
  label: string;
  input: 'text' | 'textarea' | 'date' | 'number' | 'checkbox';
  placeholder?: string;
  rows?: number;
}

export interface LeadCaseModuleDefinition {
  key: LeadCaseModuleKey;
  label: string;
  description: string;
  practiceAreaHints: string[];
  fields: LeadCaseModuleFieldDefinition[];
}

export const LEAD_CASE_MODULE_DEFINITIONS: LeadCaseModuleDefinition[] = [
  {
    key: 'family_law',
    label: 'Family Law Details',
    description: 'Capture spouse, children, support, and custody facts that shape family-law strategy.',
    practiceAreaHints: ['family', 'divorce', 'custody', 'support', 'domestic'],
    fields: [
      { key: 'spouseNames', label: 'Spouse / Ex-Spouse Names', input: 'textarea', rows: 2, placeholder: 'Names of spouse, ex-spouse, or partner' },
      { key: 'marriageDate', label: 'Marriage Date', input: 'date' },
      { key: 'separationDate', label: 'Separation Date', input: 'date' },
      { key: 'childCount', label: 'Number of Children', input: 'number', placeholder: '0' },
      { key: 'childrenDetails', label: 'Children Names / Ages', input: 'textarea', rows: 3, placeholder: 'Names, ages, and any special concerns' },
      { key: 'custodyStatus', label: 'Custody Status', input: 'text', placeholder: 'Current custody arrangement or dispute' },
      { key: 'supportStatus', label: 'Support Status', input: 'text', placeholder: 'Child or spousal support details' },
      { key: 'domesticViolenceConcerns', label: 'DV Concerns', input: 'textarea', rows: 3, placeholder: 'Protective orders, safety issues, or threats' },
    ],
  },
  {
    key: 'civil_litigation',
    label: 'Civil Litigation Details',
    description: 'Track the core civil dispute, exposure, and remedy the client is seeking.',
    practiceAreaHints: ['civil', 'litigation', 'dispute', 'contract', 'tort'],
    fields: [
      { key: 'disputeType', label: 'Dispute Type', input: 'text', placeholder: 'Contract, property, negligence, business dispute' },
      { key: 'disputeDetails', label: 'Dispute Details', input: 'textarea', rows: 4, placeholder: 'Key facts, allegations, and timeline' },
      { key: 'opposingPartyType', label: 'Opposing Party Type', input: 'text', placeholder: 'Individual, company, landlord, agency' },
      { key: 'disputedAmount', label: 'Disputed Amount', input: 'text', placeholder: '$ amount or estimated exposure' },
      { key: 'disputeStage', label: 'Dispute Stage', input: 'text', placeholder: 'Pre-suit, demand letter, filed case, appeal' },
      { key: 'reliefSought', label: 'Relief Sought', input: 'textarea', rows: 3, placeholder: 'Damages, injunction, settlement goals' },
    ],
  },
  {
    key: 'personal_injury_workers_comp',
    label: 'PI / Workers’ Comp Details',
    description: 'Capture injury, claim, treatment, and wage-loss details early.',
    practiceAreaHints: ['personal injury', 'workers comp', 'workers compensation', 'injury', 'pi'],
    fields: [
      { key: 'employerName', label: 'Employer', input: 'text', placeholder: 'Employer name if work-related' },
      { key: 'wageLoss', label: 'Wage Loss', input: 'text', placeholder: 'Missed work, reduced hours, or lost income' },
      { key: 'insurer', label: 'Insurer', input: 'text', placeholder: 'Carrier name' },
      { key: 'claimNumber', label: 'Claim Number', input: 'text', placeholder: 'Claim or policy number' },
      { key: 'injuries', label: 'Injuries', input: 'textarea', rows: 3, placeholder: 'Body parts, symptoms, diagnosis' },
      { key: 'providers', label: 'Providers', input: 'textarea', rows: 2, placeholder: 'Doctors, clinics, hospitals' },
      { key: 'treatmentStatus', label: 'Treatment Status', input: 'text', placeholder: 'Ongoing, discharged, surgery pending' },
    ],
  },
  {
    key: 'employment',
    label: 'Employment Matter Details',
    description: 'Track employer, supervisor, pay, and complaint details for employment disputes.',
    practiceAreaHints: ['employment', 'wrongful termination', 'workplace', 'labor', 'hr'],
    fields: [
      { key: 'employerName', label: 'Employer', input: 'text', placeholder: 'Employer or company name' },
      { key: 'supervisorName', label: 'Supervisor', input: 'text', placeholder: 'Supervisor or manager name' },
      { key: 'payTypeRate', label: 'Pay Type / Rate', input: 'text', placeholder: 'Salary, hourly rate, commission' },
      { key: 'employmentStartDate', label: 'Employment Start Date', input: 'date' },
      { key: 'employmentEndDate', label: 'Employment End Date', input: 'date' },
      { key: 'unionStatus', label: 'Union Status', input: 'text', placeholder: 'Union member, non-union, unknown' },
      { key: 'hrComplaintMade', label: 'HR Complaint Made', input: 'checkbox' },
    ],
  },
  {
    key: 'criminal',
    label: 'Criminal Matter Details',
    description: 'Capture arrest, charges, custody, and prior-history details for criminal intake.',
    practiceAreaHints: ['criminal', 'dui', 'felony', 'misdemeanor', 'arrest'],
    fields: [
      { key: 'arrestDate', label: 'Arrest Date', input: 'date' },
      { key: 'charges', label: 'Charges', input: 'textarea', rows: 3, placeholder: 'Current charges or citations' },
      { key: 'custodyStatus', label: 'Custody Status', input: 'text', placeholder: 'In custody, released, probation, warrant' },
      { key: 'bond', label: 'Bond', input: 'text', placeholder: 'Bond amount or status' },
      { key: 'priorArrests', label: 'Prior Arrests', input: 'textarea', rows: 2, placeholder: 'History or prior record details' },
    ],
  },
  {
    key: 'immigration',
    label: 'Immigration Details',
    description: 'Capture immigration identifiers, dependents, and sponsor context up front.',
    practiceAreaHints: ['immigration', 'visa', 'green card', 'citizenship', 'deportation'],
    fields: [
      { key: 'citizenshipStatus', label: 'Citizenship / Status', input: 'text', placeholder: 'Citizen, permanent resident, visa holder' },
      { key: 'aNumber', label: 'A-Number', input: 'text', placeholder: 'Alien registration number' },
      { key: 'dependents', label: 'Dependents', input: 'textarea', rows: 2, placeholder: 'Dependents and relevant status info' },
      { key: 'sponsorOrEmployer', label: 'Sponsor / Employer', input: 'text', placeholder: 'Sponsor, petitioner, or employer' },
    ],
  },
  {
    key: 'estate_probate',
    label: 'Estate / Probate Details',
    description: 'Capture heirs, assets, property, and fiduciary roles relevant to estate matters.',
    practiceAreaHints: ['estate', 'probate', 'trust', 'will', 'elder'],
    fields: [
      { key: 'spouseChildren', label: 'Spouse / Children', input: 'textarea', rows: 2, placeholder: 'Family members and relationships' },
      { key: 'assets', label: 'Assets', input: 'textarea', rows: 3, placeholder: 'Bank accounts, investments, vehicles, business interests' },
      { key: 'property', label: 'Property', input: 'textarea', rows: 2, placeholder: 'Real estate or major property holdings' },
      { key: 'executorTrusteeNames', label: 'Executor / Trustee Names', input: 'textarea', rows: 2, placeholder: 'Current or proposed fiduciaries' },
    ],
  },
];

export const LEAD_CASE_MODULE_DEFINITION_MAP = LEAD_CASE_MODULE_DEFINITIONS.reduce((acc, definition) => {
  acc[definition.key] = definition;
  return acc;
}, {} as Record<LeadCaseModuleKey, LeadCaseModuleDefinition>);

export function resolveLeadCaseModuleKeys(practiceAreas: string[]): LeadCaseModuleKey[] {
  const matches = new Set<LeadCaseModuleKey>();

  for (const practiceArea of practiceAreas) {
    const normalized = String(practiceArea || '').trim().toLowerCase();
    if (!normalized) {
      continue;
    }

    for (const definition of LEAD_CASE_MODULE_DEFINITIONS) {
      if (definition.practiceAreaHints.some((hint) => normalized.includes(hint))) {
        matches.add(definition.key);
      }
    }
  }

  return Array.from(matches);
}

export function sanitizeLeadCaseModules(
  caseModules: LeadCaseModules,
  selectedModuleKeys: LeadCaseModuleKey[],
): LeadCaseModules | undefined {
  const cleaned: LeadCaseModules = {};

  for (const moduleKey of selectedModuleKeys) {
    const moduleValues = caseModules[moduleKey];
    if (!moduleValues) {
      continue;
    }

    const nextValues: Record<string, LeadCaseModuleFieldValue> = {};
    for (const field of LEAD_CASE_MODULE_DEFINITION_MAP[moduleKey].fields) {
      const value = moduleValues[field.key];
      if (value === undefined || value === null || value === '') {
        continue;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          nextValues[field.key] = trimmed;
        }
        continue;
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        nextValues[field.key] = value;
      }
    }

    if (Object.keys(nextValues).length > 0) {
      cleaned[moduleKey] = nextValues;
    }
  }

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function ensureLeadCaseModules(
  selectedModuleKeys: LeadCaseModuleKey[],
  existingModules?: LeadCaseModules,
): LeadCaseModules {
  const nextModules: LeadCaseModules = {};

  for (const moduleKey of selectedModuleKeys) {
    nextModules[moduleKey] = { ...(existingModules?.[moduleKey] || {}) };
  }

  return nextModules;
}
